import React from 'react';

import { BarraProgresso, Chip, Nota } from '../../ui';
import { formatarDataHora } from '../../utils/data';
import { estadoDaBarra, rotuloOrigemExecucao, rotuloStatusExecucao } from '../../utils/syncExecucao';

/**
 * Estado da última execução de uma sincronização (at-67, frente D/D5).
 *
 * 🔴 **Existe porque o mesmo bloco estava desenhado TRÊS vezes à mão** — em
 * `ModalSincronizarCotas`, `ModalSincronizarProdutos` e
 * `ModalDescobertaAssociados` —, cada um com a própria `<div style={{ border,
 * borderRadius, padding }}>`, a própria barra de progresso em `style` com cores
 * hardcoded (`#f97316`/`#16a34a`/`#dc2626`) e o próprio mapa de status com emoji.
 * São 38 blocos `style={{}}` somados.
 *
 * ⚠️ **Mora em `components/`, não em `src/ui`** (regra de camada do
 * `13-ui-kit.md`): ele conhece o vocabulário das runs do SAAC — os status
 * `EM_ANDAMENTO`/`CONCLUIDO`/`FALHA`/`INTERROMPIDO`, a `origem` CRON/MANUAL, a
 * ideia de "fase". O que ele usa do kit (`Chip`, `Nota`, `BarraProgresso`) é puro.
 * Precedente: `ShellSaac`, `StepperAdesao`, `PainelExecucaoSync`.
 *
 * ⚠️ **Componente burro:** não busca, não faz polling e não sabe o que os
 * contadores significam — quem monta o `resumo` e os `avisos` é o modal, porque
 * cada sincronização conta coisas diferentes (cotas atualizadas × produtos
 * espelhados × associados importados).
 */

export interface PropsPainelExecucaoSync {
  /** `EM_ANDAMENTO` | `CONCLUIDO` | `FALHA` | `INTERROMPIDO` (ou o que vier). */
  status: string;
  /** Fase já traduzida pelo consumidor ("espelhando produtos do SGA"). */
  faseRotulo?: string;
  /** `CRON` | `MANUAL`. Ausente = não se diz de onde veio. */
  origem?: string;
  iniciadoEm?: string | null;
  /** 0 a 100. */
  percentual: number;
  /** Mostra "feitas/total" ao lado do estado. Omitido quando `total` é 0. */
  progresso?: { feitas: number; total: number };
  /** Os contadores da execução, montados pelo modal. */
  resumo?: React.ReactNode;
  /** Mensagem de erro da run (o campo `erro` da tabela). */
  erro?: string | null;
  /**
   * Avisos que pedem ação humana (cotas sem vínculo, associados fora de
   * carteira, cadastros incompletos no SGA).
   *
   * ⚠️ São `Nota atencao` (âmbar), não vermelho: é trabalho a fazer, não falha da
   * execução — a mesma distinção que o `Chip` faz entre `pendente` e `perigo`.
   */
  avisos?: React.ReactNode[];
}

const PainelExecucaoSync: React.FC<PropsPainelExecucaoSync> = ({
  status,
  faseRotulo,
  origem,
  iniciadoEm,
  percentual,
  progresso,
  resumo,
  erro,
  avisos = [],
}) => {
  const rotulo = rotuloStatusExecucao(status);
  const emAndamento = status === 'EM_ANDAMENTO';

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white tw-p-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
        <Chip variante={rotulo.variante}>{rotulo.texto}</Chip>

        {/* A fase só diz algo enquanto a execução corre — depois dela, "fase:
            VINCULOS" descreve um passado que já não muda nada. */}
        {emAndamento && faseRotulo && (
          <span className="tw-text-xs tw-text-texto-suave">{faseRotulo}</span>
        )}

        {progresso && progresso.total > 0 && (
          <span className="tw-ml-auto tw-text-xs tw-font-semibold tw-text-texto-corpo">
            {progresso.feitas}/{progresso.total}
          </span>
        )}
      </div>

      <BarraProgresso percent={percentual} estado={estadoDaBarra(status)} semMoldura />

      <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
        {origem && <>Disparo {rotuloOrigemExecucao(origem)} · </>}
        {iniciadoEm ? formatarDataHora(iniciadoEm) : 'sem data de início'}
      </p>

      {resumo && <div className="tw-text-xs tw-text-texto-suave">{resumo}</div>}

      {/* ⚠️ O erro da execução é `ErrorState`? Não: ali não há o que "tentar
          novamente" nesta caixa (o botão de disparo é de fora), e o conteúdo do
          painel continua válido — é o desfecho de uma execução passada. */}
      {erro && (
        <p className="tw-m-0 tw-text-xs tw-font-semibold tw-text-danger">Erro: {erro}</p>
      )}

      {avisos.map((aviso, indice) => (
        // eslint-disable-next-line react/no-array-index-key
        <Nota key={indice} variante="atencao">
          {aviso}
        </Nota>
      ))}
    </div>
  );
};

export default PainelExecucaoSync;
